import React from 'react';

let nextInputId = 0;

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    label?: string;
    icon?: React.ReactNode;
    leftIcon?: string | React.ReactNode;
    rightIcon?: React.ReactNode;
    rightAdornment?: React.ReactNode;
    className?: string;
    inputClassName?: string;
    hint?: string;
    error?: string;
    size?: 'sm' | 'md';
    labelClassName?: string;
}

const baseFieldClasses = 'w-full rounded-xl border border-secondary bg-surface text-content shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-60';
const sizeFieldClasses = {
    sm: 'min-h-10 px-3 py-2 text-sm',
    md: 'min-h-11 px-3.5 py-3 text-sm',
};

export const getTextControlClassName = ({
    size = 'md',
    hasLeftIcon = false,
    hasRightAdornment = false,
    className = '',
}: {
    size?: 'sm' | 'md';
    hasLeftIcon?: boolean;
    hasRightAdornment?: boolean;
    className?: string;
}) => {
    return [
        baseFieldClasses,
        sizeFieldClasses[size],
        hasLeftIcon ? 'pl-11' : '',
        hasRightAdornment ? 'pr-11' : '',
        className,
    ].filter(Boolean).join(' ');
};

export const selectControlClassName = 'w-full min-h-11 rounded-xl border border-secondary bg-surface px-3.5 py-3 text-sm text-content shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 focus:ring-offset-surface';
export const textareaControlClassName = 'w-full rounded-xl border border-secondary bg-surface px-3.5 py-3 text-sm text-content shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-60';

const Input: React.FC<InputProps> = ({
    label,
    icon,
    leftIcon,
    rightIcon,
    rightAdornment,
    className,
    inputClassName,
    hint,
    error,
    size = 'md',
    labelClassName,
    id,
    'aria-describedby': ariaDescribedBy,
    ...props
}) => {
    const iconToShow = leftIcon || icon;
    const hasRightAdornment = Boolean(rightAdornment || rightIcon);
    const generatedIdRef = React.useRef<string | null>(null);
    if (!generatedIdRef.current) {
        nextInputId += 1;
        generatedIdRef.current = `flowium-input-${nextInputId}`;
    }
    const inputId = id || generatedIdRef.current;
    const supportingTextId = `${inputId}-supporting-text`;
    const describedBy = [
        ariaDescribedBy,
        error || hint ? supportingTextId : '',
    ].filter(Boolean).join(' ') || undefined;

    return (
        <div className={`flex w-full flex-col ${className || ''}`}>
            {label && (
                <label
                    htmlFor={inputId}
                    className={`mb-1.5 text-sm font-medium text-content ${labelClassName || ''}`}
                >
                    {label}
                </label>
            )}
            <div className="relative">
                {iconToShow && (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-content-tertiary">
                        {iconToShow}
                    </div>
                )}
                <input
                    {...props}
                    id={inputId}
                    className={getTextControlClassName({
                        size,
                        hasLeftIcon: Boolean(iconToShow),
                        hasRightAdornment,
                        className: inputClassName,
                    })}
                    aria-describedby={describedBy}
                    aria-invalid={error ? true : undefined}
                />
                {rightAdornment && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        {rightAdornment}
                    </div>
                )}
                {!rightAdornment && rightIcon && (
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-content-tertiary">
                        {rightIcon}
                    </div>
                )}
            </div>
            {error ? (
                <p id={supportingTextId} className="mt-1.5 text-sm text-error">{error}</p>
            ) : hint ? (
                <p id={supportingTextId} className="mt-1.5 text-sm text-content-secondary">{hint}</p>
            ) : null}
        </div>
    );
};

export default Input;
